import { milestonesFor, type ProgressReport } from '@light2live/shared-types';

/**
 * What the client has earned by turning up (#239).
 *
 * Every milestone here is about the ACT of reporting — one sent, four sent, a
 * month of them — and none is about a number moving the right way. That is the
 * hard part of the feature and it is deliberate: a client whose weight went up
 * and whose symptoms got worse still filed four reports, and saying so is
 * honest. Congratulating them on progress they did not make is not, and this
 * audience will notice.
 *
 * #239 says #216 and #210 land first because the streak reads the wrong source
 * and goals-met is computed wrong. Neither is involved here — these are counted
 * off the reports themselves — so this does not wait on them, and it does not
 * touch the streak the rest of that issue is about.
 */
export function Milestones({ reports }: { reports: ProgressReport[] }) {
    const earned = milestonesFor(reports);
    if (earned.length === 0) return null;

    // The newest first: the one just unlocked is the one worth reading.
    const ordered = [...earned].reverse();

    return (
        <section
            aria-labelledby="milestones-heading"
            className="rounded-2xl border border-brand-surface-edge bg-brand-surface p-5 shadow-sm sm:p-6"
        >
            <h2
                id="milestones-heading"
                className="text-sm font-semibold tracking-wide text-brand-text-green uppercase"
            >
                What you have kept up
            </h2>

            <ul className="mt-3 space-y-3">
                {ordered.map((milestone) => (
                    <li key={milestone.key} className="flex items-start gap-3">
                        {/*
                            A brand fill carrying near-black, which is the only
                            way #94c740 is legible: as text on this card it is
                            2.00:1, as a fill under #1a1a1a it is 8.70:1.

                            aria-hidden because the tick is decoration — the item
                            is in the list at all only because it was earned, so
                            announcing "tick" would add nothing.
                        */}
                        <span
                            aria-hidden="true"
                            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-fill-lime text-base font-bold text-brand-fill-ink"
                        >
                            ✓
                        </span>
                        <span>
                            <span className="block text-base font-bold text-gray-900">
                                {milestone.title}
                            </span>
                            <span className="block text-base text-gray-700">
                                {milestone.detail}
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
}
